import type { ModuleSearchResult } from '@/components/time-entry/LinkWorkSelector'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useEffect, useState } from 'react'
import { LinkWorkSelector } from '@/components/time-entry/LinkWorkSelector'
import { TimeEntryTagging } from '@/components/time-entry/TimeEntryTagging'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  useDeleteTimeEntryMutation,
  useSplitTimeEntryMutation,
} from '@/hooks/mutations/useTimeEntryMutations'
import { useTimeEntryQuery } from '@/hooks/queries/useTimeEntriesQuery'
import { cn } from '@/lib/utils'
import { getModuleLinkMeta } from '@/modules/module-link'
import { CreateTimeEntryDialog } from '@/pages/time/_components/CreateTimeEntryDialog'
import {
  ArrowCounterClockwiseIcon,
  ArrowsSplitIcon,
  CircleNotchIcon,
  DotsThreeIcon,
  InfoIcon,
  NotePencilIcon,
  PencilSimpleIcon,
  PlusIcon,
  UsersIcon,
  XIcon,
} from '@phosphor-icons/react'
import { toast } from 'sonner'

import type {
  SplitTimeEntryInput,
  TimeEntry,
  TimeEntryLinkedModule,
} from '@scrambl/shared'
import { formatShortcutKeys } from '@/utils/keyboard-utils'

interface SplitTimeEntryDialogProps {
  open: boolean
  entry: TimeEntry | null
  onOpenChange: (open: boolean) => void
  beforeSplit?: () => Promise<TimeEntry | null>
  onSplitComplete?: (entries: TimeEntry[]) => void
  requireCompletion?: boolean
  allowCancel?: boolean
  onCancel?: () => void
  totalMinutesOverride?: number
  linkedModulesOverride?: TimeEntryLinkedModule[]
  existingEntries?: TimeEntry[]
  allowedModuleTypes?: string[]
  pendingDescription?: string
  pendingDescriptionRequired?: boolean
  onPendingDescriptionChange?: (description: string) => void
  title?: string
  subtitle?: string
}

// 'even' edits raw minutes, 'hours' edits decimal hours (minutes stay the
// source of truth), 'percentage' edits shares of the total.
type SplitMode = 'even' | 'hours' | 'percentage'

const formatMinutes = (minutes: number) => {
  if (minutes <= 0) return '0m'
  const hours = Math.floor(minutes / 60)
  const remaining = minutes % 60
  if (hours === 0) return `${remaining}m`
  if (remaining === 0) return `${hours}h`
  return `${hours}h ${remaining}m`
}

const round = (value: number, decimals: number) => {
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

function InfoHint({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={450} skipDelayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="More information"
          >
            <InfoIcon className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[240px]">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

const getEntryMinutes = (entry: TimeEntry) => {
  if (
    entry.duration !== null &&
    entry.duration !== undefined &&
    !Number.isNaN(Number(entry.duration))
  ) {
    const hours = Number(entry.duration)
    const flooredMinutes = Math.floor(hours * 60)
    return Math.max(hours > 0 ? 1 : 0, flooredMinutes)
  }

  if (!entry.startTime || !entry.endTime) {
    return 0
  }

  const diffMinutes =
    (new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime()) /
    60000
  const flooredMinutes = Math.floor(diffMinutes)
  return Math.max(diffMinutes > 0 ? 1 : 0, flooredMinutes)
}

const formatEntryWindow = (entry: TimeEntry) => {
  const formatter = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })

  if (entry.startTime && entry.endTime) {
    return `${formatter.format(new Date(entry.startTime))} - ${formatter.format(new Date(entry.endTime))}`
  }

  if (entry.startTime) {
    return `${formatter.format(new Date(entry.startTime))} - now`
  }

  return 'Saved log'
}

export function SplitTimeEntryDialog({
  open,
  entry,
  onOpenChange,
  beforeSplit,
  onSplitComplete,
  requireCompletion = false,
  allowCancel = false,
  onCancel,
  totalMinutesOverride,
  linkedModulesOverride,
  existingEntries,
  allowedModuleTypes,
  pendingDescription,
  pendingDescriptionRequired = false,
  onPendingDescriptionChange,
  title = 'Finish Timer Session',
  subtitle = "Assign this session's time to the work it belongs to.",
}: SplitTimeEntryDialogProps) {
  const splitMutation = useSplitTimeEntryMutation()
  const deleteMutation = useDeleteTimeEntryMutation()
  const { data: entryDetails } = useTimeEntryQuery(entry?.id, {
    enabled: open && !!entry?.id,
  })
  const resolvedEntry = entryDetails ?? entry
  const resolvedLinkedModules = (() => {
    if (linkedModulesOverride?.length) {
      return linkedModulesOverride
    }
    if (entryDetails?.linkedModules?.length) {
      return entryDetails.linkedModules
    }
    return entry?.linkedModules ?? []
  })()

  const [splits, setSplits] = useState<SplitTimeEntryInput['splits']>([])
  const [percentages, setPercentages] = useState<number[]>([])
  const [splitMode, setSplitMode] = useState<SplitMode>('even')
  // In-progress decimal hours text per row, so typing "1." doesn't get
  // normalized away mid-keystroke. Cleared on blur and structural changes.
  const [hoursDrafts, setHoursDrafts] = useState<Record<number, string>>({})
  const [isPreparingSplit, setIsPreparingSplit] = useState(false)
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null)
  const [sessionEntriesDraft, setSessionEntriesDraft] = useState<TimeEntry[]>(
    [],
  )
  const [removedSavedIds, setRemovedSavedIds] = useState<Set<string>>(new Set())
  const [addTargetKey, setAddTargetKey] = useState(0)
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set())
  const [expandedTaggingKey, setExpandedTaggingKey] = useState<string | null>(
    null,
  )

  const totalMinutes = (() => {
    if (
      typeof totalMinutesOverride === 'number' &&
      !Number.isNaN(totalMinutesOverride)
    ) {
      const flooredMinutes = Math.floor(totalMinutesOverride)
      return Math.max(totalMinutesOverride > 0 ? 1 : 0, flooredMinutes)
    }

    if (!resolvedEntry) return 0
    return getEntryMinutes(resolvedEntry)
  })()

  const remainingMinutes = (() => {
    const allocated = splits.reduce(
      (sum, split) => sum + split.durationMinutes,
      0,
    )
    return totalMinutes - allocated
  })()

  const totalPercentage = (() => {
    return percentages.reduce((sum, pct) => sum + pct, 0)
  })()

  const percentageValid = (() => {
    return Math.abs(totalPercentage - 100) < 0.01
  })()

  const allocatedNewMinutes = (() => {
    if (splitMode === 'percentage') {
      return Math.round(totalMinutes * (Math.min(totalPercentage, 100) / 100))
    }
    return Math.min(totalMinutes, totalMinutes - remainingMinutes)
  })()

  const isOverAllocated =
    splitMode === 'percentage'
      ? totalPercentage - 100 > 0.01
      : remainingMinutes < 0

  const resolvedExistingEntries = (() => {
    return [...sessionEntriesDraft].sort((left, right) => {
      const leftTime = left.startTime ? new Date(left.startTime).getTime() : 0
      const rightTime = right.startTime
        ? new Date(right.startTime).getTime()
        : 0
      return rightTime - leftTime
    })
  })()

  const existingLoggedMinutes = (() => {
    return resolvedExistingEntries.reduce((sum, sessionEntry) => {
      if (removedSavedIds.has(sessionEntry.id)) return sum
      return sum + getEntryMinutes(sessionEntry)
    }, 0)
  })()

  const sessionTotalMinutes = totalMinutes + existingLoggedMinutes

  const toggleRemoveSaved = (id: string) => {
    setRemovedSavedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const getDisplayMinutes = (index: number): number => {
    if (splitMode !== 'percentage') return splits[index]?.durationMinutes ?? 0
    if (totalMinutes <= 0) return 0
    return round(totalMinutes * ((percentages[index] ?? 0) / 100), 1)
  }

  const minutesToHours = (minutes: number) => round(minutes / 60, 2)

  const getHoursInputValue = (index: number): string => {
    const draft = hoursDrafts[index]
    if (draft !== undefined) return draft
    const minutes = splits[index]?.durationMinutes ?? 0
    return minutes > 0 ? String(minutesToHours(minutes)) : ''
  }

  const getDisplayPercentage = (index: number): number => {
    if (splitMode === 'percentage') return percentages[index] ?? 0
    if (totalMinutes <= 0) return 0
    return round(
      ((splits[index]?.durationMinutes ?? 0) / totalMinutes) * 100,
      1,
    )
  }

  useEffect(() => {
    setSessionEntriesDraft(existingEntries ?? [])
    setRemovedSavedIds(new Set())
    setExpandedNotes(new Set())
    setExpandedTaggingKey(null)
  }, [existingEntries, open])

  useEffect(() => {
    if (!resolvedEntry || !open) return

    setHoursDrafts({})

    if (resolvedLinkedModules.length === 0 || totalMinutes === 0) {
      setSplits([])
      setPercentages([])
      setSplitMode('even')
      return
    }

    const baseMinutes = Math.floor(totalMinutes / resolvedLinkedModules.length)
    const remainder = totalMinutes - baseMinutes * resolvedLinkedModules.length

    setSplits(
      resolvedLinkedModules.map((module, index) => ({
        moduleType: module.moduleType,
        moduleId: module.moduleId,
        moduleName: module.moduleName ?? null,
        durationMinutes: baseMinutes + (index < remainder ? 1 : 0),
        taggedUserIds: [],
      })),
    )

    const evenPct = round(100 / resolvedLinkedModules.length, 2)
    setPercentages(
      resolvedLinkedModules.map((_, index) =>
        index === resolvedLinkedModules.length - 1
          ? round(100 - evenPct * (resolvedLinkedModules.length - 1), 2)
          : evenPct,
      ),
    )
    setSplitMode('even')
  }, [open, resolvedEntry, resolvedLinkedModules, totalMinutes])

  const handleMinutesChange = (index: number, value: string) => {
    const trimmed = value.trim()
    const raw =
      trimmed === '' ? 0 : Number.isNaN(Number(value)) ? 0 : Number(value)
    const minutes = Math.max(0, Math.round(raw))

    setSplits(prev =>
      prev.map((split, idx) =>
        idx === index ? { ...split, durationMinutes: minutes } : split,
      ),
    )
  }

  const handleHoursChange = (index: number, value: string) => {
    setHoursDrafts(prev => ({ ...prev, [index]: value }))
    const trimmed = value.trim()
    const raw =
      trimmed === '' ? 0 : Number.isNaN(Number(value)) ? 0 : Number(value)
    const minutes = Math.max(0, Math.round(raw * 60))

    setSplits(prev =>
      prev.map((split, idx) =>
        idx === index ? { ...split, durationMinutes: minutes } : split,
      ),
    )
  }

  const handleHoursBlur = (index: number) => {
    setHoursDrafts(prev => {
      const next = { ...prev }
      delete next[index]
      return next
    })
  }

  const handleDescriptionChange = (index: number, value: string) => {
    setSplits(prev =>
      prev.map((split, idx) =>
        idx === index ? { ...split, description: value } : split,
      ),
    )
  }

  const handleTaggedUserIdsChange = (index: number, userIds: string[]) => {
    setSplits(prev =>
      prev.map((split, idx) =>
        idx === index ? { ...split, taggedUserIds: userIds } : split,
      ),
    )
  }

  const toggleTagging = (key: string) => {
    setExpandedTaggingKey(current => (current === key ? null : key))
  }

  const toggleNote = (key: string) => {
    setExpandedNotes(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const handleFillRemaining = (index: number) => {
    if (remainingMinutes <= 0) return

    setHoursDrafts({})
    setSplits(prev =>
      prev.map((split, currentIndex) =>
        currentIndex === index
          ? {
              ...split,
              durationMinutes: split.durationMinutes + remainingMinutes,
            }
          : split,
      ),
    )
  }

  const handleSplitRemaining = () => {
    if (remainingMinutes <= 0 || splits.length === 0) return

    setHoursDrafts({})
    const blankIndexes = splits
      .map((split, index) => (split.durationMinutes <= 0 ? index : -1))
      .filter(index => index >= 0)
    const targetIndexes =
      blankIndexes.length > 0 ? blankIndexes : splits.map((_, index) => index)
    const baseMinutes = Math.floor(remainingMinutes / targetIndexes.length)
    const remainder = remainingMinutes - baseMinutes * targetIndexes.length

    setSplits(prev =>
      prev.map((split, index) => {
        const targetIndex = targetIndexes.indexOf(index)
        if (targetIndex === -1) return split
        return {
          ...split,
          durationMinutes:
            split.durationMinutes +
            baseMinutes +
            (targetIndex < remainder ? 1 : 0),
        }
      }),
    )
  }

  const handleAddSplitTarget = (result: ModuleSearchResult | null) => {
    if (!result) return

    const alreadyAdded = splits.some(
      split => split.moduleType === result.type && split.moduleId === result.id,
    )
    if (alreadyAdded) {
      toast.error('That item is already in this split.')
      return
    }

    setHoursDrafts({})
    setSplits(prev => [
      ...prev,
      {
        moduleType:
          result.type as SplitTimeEntryInput['splits'][number]['moduleType'],
        moduleId: result.id,
        moduleName: result.name,
        durationMinutes: Math.max(0, remainingMinutes),
        taggedUserIds: [],
      },
    ])
    setAddTargetKey(key => key + 1)
  }

  const handlePercentageChange = (index: number, value: string) => {
    const trimmed = value.trim()
    const raw =
      trimmed === '' ? 0 : Number.isNaN(Number(value)) ? 0 : Number(value)
    const pct = Math.max(0, round(raw, 2))

    setPercentages(prev =>
      prev.map((current, idx) => (idx === index ? pct : current)),
    )
    const minutes = Math.round(totalMinutes * (pct / 100))
    setSplits(prev =>
      prev.map((split, idx) =>
        idx === index ? { ...split, durationMinutes: minutes } : split,
      ),
    )
  }

  const handleModeChange = (value: string) => {
    if (!value || value === splitMode) return
    const nextMode = value as SplitMode
    setHoursDrafts({})

    if (nextMode === 'percentage') {
      if (totalMinutes > 0) {
        const nextPercentages = splits.map(split =>
          round((split.durationMinutes / totalMinutes) * 100, 2),
        )
        const total = nextPercentages.reduce((sum, pct) => sum + pct, 0)
        if (nextPercentages.length > 0) {
          nextPercentages[nextPercentages.length - 1] = round(
            nextPercentages[nextPercentages.length - 1] + (100 - total),
            2,
          )
        }
        setPercentages(nextPercentages)
      }
    } else if (
      // Minutes and hours share the same backing values — only recompute
      // minutes when actually leaving percentage mode.
      splitMode === 'percentage' &&
      totalMinutes > 0 &&
      percentages.length > 0
    ) {
      const nextMinutes = percentages.map(pct =>
        Math.round(totalMinutes * (pct / 100)),
      )
      const allocated = nextMinutes.reduce((sum, minutes) => sum + minutes, 0)
      if (nextMinutes.length > 0) {
        nextMinutes[nextMinutes.length - 1] += totalMinutes - allocated
      }
      setSplits(prev =>
        prev.map((split, index) => ({
          ...split,
          durationMinutes: nextMinutes[index],
        })),
      )
    }

    setSplitMode(nextMode)
  }

  const handleEvenSplit = () => {
    if (!splits.length || totalMinutes <= 0) return

    setHoursDrafts({})
    if (splitMode !== 'percentage') {
      const baseMinutes = Math.floor(totalMinutes / splits.length)
      const remainder = totalMinutes - baseMinutes * splits.length
      setSplits(prev =>
        prev.map((split, index) => ({
          ...split,
          durationMinutes: baseMinutes + (index < remainder ? 1 : 0),
        })),
      )
      return
    }

    const evenPct = round(100 / splits.length, 2)
    const nextPercentages = splits.map((_, index) =>
      index === splits.length - 1
        ? round(100 - evenPct * (splits.length - 1), 2)
        : evenPct,
    )
    setPercentages(nextPercentages)

    const nextMinutes = nextPercentages.map(pct =>
      Math.round(totalMinutes * (pct / 100)),
    )
    const allocated = nextMinutes.reduce((sum, minutes) => sum + minutes, 0)
    if (nextMinutes.length > 0) {
      nextMinutes[nextMinutes.length - 1] += totalMinutes - allocated
    }
    setSplits(prev =>
      prev.map((split, index) => ({
        ...split,
        durationMinutes: nextMinutes[index],
      })),
    )
  }

  const handleRemoveSplit = (index: number) => {
    setHoursDrafts({})
    setSplits(prev => prev.filter((_, currentIndex) => currentIndex !== index))
    setPercentages(prev => {
      const nextPercentages = prev.filter(
        (_, currentIndex) => currentIndex !== index,
      )

      if (nextPercentages.length === 0) {
        return []
      }

      const total = nextPercentages.reduce((sum, pct) => sum + pct, 0)
      if (total <= 0) {
        const evenPct = round(100 / nextPercentages.length, 2)
        return nextPercentages.map((_, currentIndex) =>
          currentIndex === nextPercentages.length - 1
            ? round(100 - evenPct * (nextPercentages.length - 1), 2)
            : evenPct,
        )
      }

      const normalized = nextPercentages.map(pct =>
        round((pct / total) * 100, 2),
      )
      const normalizedTotal = normalized.reduce((sum, pct) => sum + pct, 0)
      normalized[normalized.length - 1] = round(
        normalized[normalized.length - 1] + (100 - normalizedTotal),
        2,
      )
      return normalized
    })
  }

  const handleSubmit = async () => {
    if (!resolvedEntry) return
    if (!splits.length) {
      toast.error('Add at least one linked item to split this entry.')
      return
    }

    let finalSplits = splits.map(split => ({ ...split }))

    if (splitMode === 'percentage') {
      const nextMinutes = percentages.map(pct =>
        Math.round(totalMinutes * (pct / 100)),
      )
      const allocated = nextMinutes.reduce((sum, minutes) => sum + minutes, 0)
      if (nextMinutes.length > 0) {
        nextMinutes[nextMinutes.length - 1] += totalMinutes - allocated
      }

      finalSplits = splits.map((split, index) => ({
        ...split,
        durationMinutes: nextMinutes[index],
      }))
    }

    if (finalSplits.some(split => split.durationMinutes <= 0)) {
      toast.error('Each split must be at least 1 minute.')
      return
    }

    const totalAllocated = finalSplits.reduce(
      (sum, split) => sum + split.durationMinutes,
      0,
    )
    if (totalAllocated <= 0) {
      toast.error('Total split time must be greater than zero.')
      return
    }

    let splitTarget = resolvedEntry

    const idsToDelete = [...removedSavedIds]

    setIsPreparingSplit(true)
    try {
      if (idsToDelete.length > 0) {
        await Promise.all(
          idsToDelete.map(id => deleteMutation.mutateAsync(id)),
        )
      }

      if (beforeSplit) {
        const preparedEntry = await beforeSplit()
        if (!preparedEntry) {
          toast.error('Unable to finalize this timer for splitting.')
          return
        }
        splitTarget = preparedEntry
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to finalize this timer.',
      )
      return
    } finally {
      setIsPreparingSplit(false)
    }

    splitMutation.mutate(
      {
        id: splitTarget.id,
        data: { splits: finalSplits },
      },
      {
        onSuccess: entries => {
          onSplitComplete?.(entries)
          onOpenChange(false)
        },
      },
    )
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && requireCompletion && !allowCancel) {
      toast.error('Split this time entry to finish.')
      return
    }
    if (!nextOpen && allowCancel) {
      onCancel?.()
    }
    onOpenChange(nextOpen)
  }

  const isSubmitDisabled =
    isPreparingSplit ||
    splitMutation.isPending ||
    splits.length === 0 ||
    splits.some(split => split.durationMinutes <= 0) ||
    Boolean(
      pendingDescriptionRequired &&
        onPendingDescriptionChange &&
        !pendingDescription?.trim(),
    )

  const isReadyToSave =
    !isSubmitDisabled && (splitMode !== 'percentage' || percentageValid)

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'd') {
      event.preventDefault()
      if (
        isSubmitDisabled ||
        (splitMode === 'percentage' && !percentageValid)
      ) {
        return
      }
      void handleSubmit()
    }
  }

  const isFromLinkWorkDropdown = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    !!target.closest('[data-link-work-dropdown]')
  const isFromMemberTaggingDropdown = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    !!target.closest('[data-member-tagging-dropdown]')

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="gap-0 overflow-hidden p-0 sm:max-w-[720px]"
          hideCloseButton={requireCompletion && !allowCancel}
          onEscapeKeyDown={event => {
            if (document.querySelector('[data-member-tagging-dropdown]')) {
              event.preventDefault()
            }
          }}
          onInteractOutside={event => {
            const outsideTarget = event.detail.originalEvent.target
            if (
              isFromLinkWorkDropdown(outsideTarget) ||
              isFromMemberTaggingDropdown(outsideTarget)
            ) {
              event.preventDefault()
            }
          }}
          onPointerDownOutside={event => {
            const outsideTarget = event.detail.originalEvent.target
            if (
              isFromLinkWorkDropdown(outsideTarget) ||
              isFromMemberTaggingDropdown(outsideTarget)
            ) {
              event.preventDefault()
            }
          }}
          onKeyDown={handleKeyDown}
        >
          <DialogHeader className="shrink-0 border-b border-border/40 px-5 pb-4 pt-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-[hsl(var(--button-primary)/0.2)] via-[hsl(var(--button-primary)/0.1)] to-transparent text-[hsl(var(--button-primary-ink))] shadow-sm">
                <ArrowsSplitIcon className="h-5 w-5" weight="duotone" />
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-base font-semibold">
                  {title}
                </DialogTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {subtitle}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="shrink-0 border-b border-border/40 bg-muted/20 px-5 py-3.5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1.5">
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-semibold tabular-nums tracking-tight">
                  {formatMinutes(sessionTotalMinutes)}
                </span>
                <span className="text-xs text-muted-foreground">
                  session total
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-muted-foreground">
                {existingLoggedMinutes > 0 ? (
                  <span className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    {formatMinutes(existingLoggedMinutes)} saved
                  </span>
                ) : null}
                <span className="flex items-center gap-1.5">
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      isOverAllocated
                        ? 'bg-destructive'
                        : 'bg-[hsl(var(--button-primary))]',
                    )}
                  />
                  {formatMinutes(allocatedNewMinutes)} of{' '}
                  {formatMinutes(totalMinutes)} assigned
                </span>
                <span>
                  {resolvedExistingEntries.length + splits.length} allocation
                  {resolvedExistingEntries.length + splits.length === 1
                    ? ''
                    : 's'}
                </span>
              </div>
            </div>
            {sessionTotalMinutes > 0 ? (
              <div className="mt-2.5 flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
                {existingLoggedMinutes > 0 ? (
                  <div
                    className="h-full bg-emerald-500 transition-[width] duration-300"
                    style={{
                      width: `${(existingLoggedMinutes / sessionTotalMinutes) * 100}%`,
                    }}
                  />
                ) : null}
                {allocatedNewMinutes > 0 ? (
                  <div
                    className={cn(
                      'h-full transition-[width] duration-300',
                      isOverAllocated
                        ? 'bg-destructive'
                        : 'bg-[hsl(var(--button-primary))]',
                      existingLoggedMinutes > 0 && 'ml-px',
                    )}
                    style={{
                      width: `${(Math.max(0, allocatedNewMinutes) / sessionTotalMinutes) * 100}%`,
                    }}
                  />
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div>
              <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-1.5 text-sm font-semibold">
                  Time Allocations
                  <InfoHint text="Saved logs and new splits live in one list. Edit, remove, or add — then Save." />
                </div>

                <div className="flex items-center gap-2 sm:justify-end">
                  <Tabs
                    value={splitMode}
                    onValueChange={handleModeChange}
                    className="flex-1 sm:flex-none"
                  >
                    <TabsList
                      variant="pill"
                      className="grid w-full grid-cols-3"
                    >
                      <TabsTrigger value="even" className="text-xs">
                        Minutes
                      </TabsTrigger>
                      <TabsTrigger value="hours" className="text-xs">
                        Hours
                      </TabsTrigger>
                      <TabsTrigger value="percentage" className="text-xs">
                        Percentage
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 flex-shrink-0 gap-1.5 px-3 text-xs"
                      >
                        <DotsThreeIcon className="h-4 w-4" weight="bold" />
                        Actions
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem
                        onClick={handleEvenSplit}
                        disabled={splits.length === 0}
                      >
                        Distribute evenly
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={handleSplitRemaining}
                        disabled={remainingMinutes <= 0 || splits.length === 0}
                      >
                        Split remaining
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {onPendingDescriptionChange ? (
                <div className="mb-4 border-b border-dashed pb-4">
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs font-medium text-muted-foreground">
                      Remaining timer description
                      {pendingDescriptionRequired ? (
                        <span className="text-destructive"> *</span>
                      ) : null}
                    </label>
                    <InfoHint text="Description saved on the remaining timer entry that will be split." />
                  </div>
                  <Textarea
                    value={pendingDescription ?? ''}
                    onChange={event =>
                      onPendingDescriptionChange(event.target.value)
                    }
                    placeholder="What were you working on?"
                    className="mt-2 min-h-[60px] resize-none bg-background text-sm"
                  />
                </div>
              ) : null}

              <div className="divide-y divide-border/60">
                <div className="hidden items-center justify-between px-1 pb-2 sm:flex">
                  <span className="text-[11px] font-medium text-muted-foreground">
                    Link to
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="w-[72px] text-right text-[11px] font-medium text-muted-foreground">
                      {splitMode === 'hours' ? 'Time (h)' : 'Time (min)'}
                    </span>
                    <span className="w-[72px] text-right text-[11px] font-medium text-muted-foreground">
                      Share %
                    </span>
                    <span className="w-[64px]" aria-hidden />
                    <span className="w-7" aria-hidden />
                    <span className="w-7" aria-hidden />
                    <span className="w-7" aria-hidden />
                  </div>
                </div>

                {resolvedExistingEntries.map(sessionEntry => {
                  const meta = getModuleLinkMeta(sessionEntry.moduleType)
                  const Icon = meta.icon
                  const isRemoved = removedSavedIds.has(sessionEntry.id)
                  const taggingKey = `saved-${sessionEntry.id}`
                  const taggingOpen = expandedTaggingKey === taggingKey
                  const minutes = getEntryMinutes(sessionEntry)
                  const pct =
                    sessionTotalMinutes > 0 && !isRemoved
                      ? round((minutes / sessionTotalMinutes) * 100, 1)
                      : 0

                  return (
                    <div
                      key={sessionEntry.id}
                      className={cn('px-1 py-2.5', isRemoved && 'opacity-50')}
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div
                          className={cn(
                            'flex items-center gap-2 truncate text-sm font-medium sm:max-w-[260px]',
                            isRemoved && 'line-through',
                          )}
                        >
                          <TooltipProvider delayDuration={300}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  aria-label="Saved"
                                  className="h-2 w-2 flex-shrink-0 rounded-full bg-emerald-500"
                                />
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                Saved log
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <div
                            className={cn(
                              'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md',
                              meta.bgClass,
                            )}
                          >
                            <Icon
                              className={cn('h-3.5 w-3.5', meta.textClass)}
                            />
                          </div>
                          <div className="flex min-w-0 flex-col">
                            <span className="truncate">
                              {sessionEntry.moduleName ||
                                sessionEntry.moduleType}
                            </span>
                            <span
                              className={cn(
                                'truncate text-[11px] text-muted-foreground',
                                isRemoved && 'text-destructive/80',
                              )}
                            >
                              {formatEntryWindow(sessionEntry)} ·{' '}
                              {isRemoved ? 'will be deleted' : 'saved'}
                            </span>
                          </div>
                        </div>

                        <TooltipProvider delayDuration={300}>
                          <div className="flex flex-wrap items-center gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-baseline justify-end gap-1 sm:w-[72px]">
                                  <span className="text-sm tabular-nums text-muted-foreground">
                                    {splitMode === 'hours'
                                      ? minutesToHours(minutes)
                                      : minutes}
                                  </span>
                                  <span className="text-xs text-muted-foreground sm:hidden">
                                    {splitMode === 'hours' ? 'h' : 'min'}
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                Saved duration · use the pencil to edit
                              </TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-baseline justify-end gap-1 sm:w-[72px]">
                                  <span className="text-sm tabular-nums text-muted-foreground">
                                    {pct}
                                  </span>
                                  <span className="text-xs text-muted-foreground sm:hidden">
                                    %
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                Share of session total
                              </TooltipContent>
                            </Tooltip>

                            <div className="flex justify-end sm:w-[64px]">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                onClick={() => setEditingEntry(sessionEntry)}
                                aria-label="Edit saved log"
                                disabled={isRemoved}
                              >
                                <PencilSimpleIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    'h-7 w-7 text-muted-foreground hover:text-foreground',
                                    taggingOpen && 'bg-muted text-foreground',
                                  )}
                                  onClick={() => toggleTagging(taggingKey)}
                                  aria-label={`Tag team for ${sessionEntry.moduleName || sessionEntry.moduleType}`}
                                  aria-expanded={taggingOpen}
                                  disabled={isRemoved}
                                >
                                  <UsersIcon className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                Add or edit tagged team
                              </TooltipContent>
                            </Tooltip>

                            {/* spacer to align with the new-row note slot */}
                            <span
                              className="hidden h-7 w-7 sm:block"
                              aria-hidden
                            />

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    'h-7 w-7 text-muted-foreground',
                                    isRemoved
                                      ? 'hover:text-foreground'
                                      : 'hover:text-destructive',
                                  )}
                                  onClick={() =>
                                    toggleRemoveSaved(sessionEntry.id)
                                  }
                                  aria-label={
                                    isRemoved
                                      ? 'Restore saved log'
                                      : `Remove ${sessionEntry.moduleName || 'log'}`
                                  }
                                >
                                  {isRemoved ? (
                                    <ArrowCounterClockwiseIcon className="h-3.5 w-3.5" />
                                  ) : (
                                    <XIcon className="h-4 w-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                {isRemoved ? 'Restore' : 'Delete saved log'}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TooltipProvider>
                      </div>
                      {taggingOpen ? (
                        <div className="mt-2 rounded-lg border border-border/60 bg-muted/20 p-3 sm:ml-8">
                          <TimeEntryTagging
                            timeEntryId={sessionEntry.id}
                            label="Tag team (Optional)"
                            hideHelperText
                            closeOnScroll={false}
                          />
                        </div>
                      ) : null}
                    </div>
                  )
                })}

                {splits.map((split, index) => {
                  const splitKey = `${split.moduleType}-${split.moduleId}`
                  const taggingKey = `new-${splitKey}`
                  const taggingOpen = expandedTaggingKey === taggingKey
                  const noteOpen =
                    expandedNotes.has(splitKey) || Boolean(split.description)
                  return (
                    <div key={splitKey} className="px-1 py-2.5">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2 truncate text-sm font-medium sm:max-w-[260px]">
                          <TooltipProvider delayDuration={300}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  aria-label="New"
                                  className="h-2 w-2 flex-shrink-0 rounded-full border border-muted-foreground/40 bg-transparent"
                                />
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                New allocation
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {(() => {
                            const meta = getModuleLinkMeta(split.moduleType)
                            const Icon = meta.icon
                            return (
                              <div
                                className={cn(
                                  'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md',
                                  meta.bgClass,
                                )}
                              >
                                <Icon
                                  className={cn('h-3.5 w-3.5', meta.textClass)}
                                />
                              </div>
                            )
                          })()}
                          <div className="flex min-w-0 flex-col">
                            <span className="truncate">
                              {split.moduleName || split.moduleType}
                            </span>
                            <span className="truncate text-[11px] text-muted-foreground">
                              {resolvedEntry
                                ? `${formatEntryWindow(resolvedEntry)} · new`
                                : 'new allocation'}
                            </span>
                          </div>
                        </div>

                        <TooltipProvider delayDuration={300}>
                          <div className="flex flex-wrap items-center gap-2">
                            {splitMode === 'even' ? (
                              <div className="flex items-center justify-end gap-1 sm:w-[72px]">
                                <Input
                                  type="number"
                                  min={0}
                                  className="h-8 w-16 bg-background text-right text-sm tabular-nums shadow-sm"
                                  value={
                                    split.durationMinutes === 0
                                      ? ''
                                      : split.durationMinutes
                                  }
                                  onChange={e =>
                                    handleMinutesChange(index, e.target.value)
                                  }
                                />
                                <span className="text-xs text-muted-foreground sm:hidden">
                                  min
                                </span>
                              </div>
                            ) : splitMode === 'hours' ? (
                              <div className="flex items-center justify-end gap-1 sm:w-[72px]">
                                <Input
                                  type="number"
                                  min={0}
                                  step="any"
                                  className="h-8 w-16 bg-background text-right text-sm tabular-nums shadow-sm"
                                  value={getHoursInputValue(index)}
                                  onChange={e =>
                                    handleHoursChange(index, e.target.value)
                                  }
                                  onBlur={() => handleHoursBlur(index)}
                                />
                                <span className="text-xs text-muted-foreground sm:hidden">
                                  h
                                </span>
                              </div>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-baseline justify-end gap-1 sm:w-[72px]">
                                    <span className="text-sm tabular-nums text-muted-foreground">
                                      {getDisplayMinutes(index)}
                                    </span>
                                    <span className="text-xs text-muted-foreground sm:hidden">
                                      min
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                  Calculated from percentage
                                </TooltipContent>
                              </Tooltip>
                            )}

                            {splitMode === 'percentage' ? (
                              <div className="flex items-center justify-end gap-1 sm:w-[72px]">
                                <Input
                                  type="number"
                                  min={0}
                                  step="any"
                                  className="h-8 w-16 bg-background text-right text-sm tabular-nums shadow-sm"
                                  value={
                                    percentages[index] === 0
                                      ? ''
                                      : percentages[index]
                                  }
                                  onChange={e =>
                                    handlePercentageChange(
                                      index,
                                      e.target.value,
                                    )
                                  }
                                />
                                <span className="text-xs text-muted-foreground sm:hidden">
                                  %
                                </span>
                              </div>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-baseline justify-end gap-1 sm:w-[72px]">
                                    <span className="text-sm tabular-nums text-muted-foreground">
                                      {getDisplayPercentage(index)}
                                    </span>
                                    <span className="text-xs text-muted-foreground sm:hidden">
                                      %
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                  Calculated from minutes
                                </TooltipContent>
                              </Tooltip>
                            )}

                            <div className="flex justify-end sm:w-[64px]">
                              {splitMode !== 'percentage' ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground"
                                  onClick={() => handleFillRemaining(index)}
                                  disabled={remainingMinutes <= 0}
                                >
                                  Fill rest
                                </Button>
                              ) : null}
                            </div>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    'relative h-7 w-7 text-muted-foreground hover:text-foreground',
                                    (taggingOpen ||
                                      (split.taggedUserIds?.length ?? 0) > 0) &&
                                      'bg-muted text-foreground',
                                  )}
                                  onClick={() => toggleTagging(taggingKey)}
                                  aria-label={`Tag team for ${split.moduleName || split.moduleType}`}
                                  aria-expanded={taggingOpen}
                                >
                                  <UsersIcon className="h-4 w-4" />
                                  {(split.taggedUserIds?.length ?? 0) > 0 ? (
                                    <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--button-primary))] px-1 text-[9px] font-semibold leading-none text-[hsl(var(--button-primary-foreground))]">
                                      {split.taggedUserIds?.length}
                                    </span>
                                  ) : null}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                {(split.taggedUserIds?.length ?? 0) > 0
                                  ? 'Edit tagged team'
                                  : 'Tag team'}
                              </TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    'h-7 w-7 text-muted-foreground',
                                    noteOpen && 'text-foreground',
                                  )}
                                  onClick={() => toggleNote(splitKey)}
                                  aria-label={
                                    noteOpen ? 'Hide note' : 'Add a note'
                                  }
                                  aria-pressed={noteOpen}
                                >
                                  <NotePencilIcon className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                {split.description ? 'Edit note' : 'Add a note'}
                              </TooltipContent>
                            </Tooltip>

                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={() => handleRemoveSplit(index)}
                              aria-label={`Remove ${split.moduleName || split.moduleType}`}
                            >
                              <XIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </TooltipProvider>
                      </div>
                      {taggingOpen ? (
                        <div className="mt-2 rounded-lg border border-border/60 bg-muted/20 p-3 sm:ml-8">
                          <TimeEntryTagging
                            label="Tag team (Optional)"
                            selectedUserIds={split.taggedUserIds ?? []}
                            onSelectedUserIdsChange={userIds =>
                              handleTaggedUserIdsChange(index, userIds)
                            }
                            hideHelperText
                            closeOnScroll={false}
                          />
                        </div>
                      ) : null}
                      {noteOpen ? (
                        <div className="mt-2 pl-8">
                          <Textarea
                            value={split.description ?? ''}
                            onChange={event =>
                              handleDescriptionChange(index, event.target.value)
                            }
                            placeholder="Add a description for this item (optional)"
                            className="min-h-[56px] resize-none bg-background text-sm"
                          />
                        </div>
                      ) : null}
                    </div>
                  )
                })}

                {resolvedExistingEntries.length === 0 && splits.length === 0 ? (
                  <div className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
                    No allocations yet — search below to add one.
                  </div>
                ) : null}
              </div>

              <div className="mt-3 border-t border-dashed pt-3">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <PlusIcon className="h-3.5 w-3.5" />
                  Add allocation
                  <InfoHint text="Search for a client, project, task, or task group to allocate the remaining timer to." />
                </div>
                <LinkWorkSelector
                  key={addTargetKey}
                  value={null}
                  onSelect={handleAddSplitTarget}
                  allowedModuleTypes={allowedModuleTypes}
                  defaultModule="ALL"
                  portalToBody
                  closeOnScroll={false}
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex items-center justify-between border-t pt-3 text-sm">
              <div className="text-muted-foreground">Remaining to assign</div>
              <div
                className={cn(
                  'font-semibold tabular-nums',
                  (
                    splitMode === 'percentage'
                      ? percentageValid
                      : remainingMinutes === 0
                  )
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : isOverAllocated
                      ? 'text-destructive'
                      : 'text-amber-600 dark:text-amber-400',
                )}
              >
                {splitMode === 'percentage'
                  ? `${round(100 - totalPercentage, 2)}%`
                  : splitMode === 'hours'
                    ? `${minutesToHours(remainingMinutes)} h`
                    : `${remainingMinutes} min`}
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t border-border/40 bg-background px-5 py-4">
            <div className="flex w-full items-center justify-between gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <span
                  className={cn(
                    'h-1.5 w-1.5 flex-shrink-0 rounded-full',
                    isReadyToSave ? 'bg-emerald-500' : 'bg-amber-500',
                  )}
                />
                {splitMode === 'percentage' && !percentageValid ? (
                  <span>Total allocation must equal 100%</span>
                ) : pendingDescriptionRequired &&
                  onPendingDescriptionChange &&
                  !pendingDescription?.trim() ? (
                  <span>Add a description for the remaining timer</span>
                ) : remainingMinutes !== 0 && splitMode !== 'percentage' ? (
                  <span>All time must be assigned before saving</span>
                ) : (
                  <span>Ready to save</span>
                )}
                {isReadyToSave ? (
                  <span className="hidden items-center gap-1 sm:inline-flex">
                    <kbd className="rounded border border-border bg-muted px-1.5 py-px font-sans text-[10px] font-medium text-muted-foreground">
                      {formatShortcutKeys(['Mod', 'D'])}
                    </kbd>
                    to save
                  </span>
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                {allowCancel ? (
                  <Button
                    variant="ghost"
                    onClick={() => handleOpenChange(false)}
                  >
                    Cancel
                  </Button>
                ) : null}
                <Button
                  onClick={() => void handleSubmit()}
                  disabled={
                    isSubmitDisabled ||
                    (splitMode === 'percentage' && !percentageValid)
                  }
                  className="min-w-[140px]"
                >
                  {isPreparingSplit || splitMutation.isPending ? (
                    <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Save Split
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateTimeEntryDialog
        open={!!editingEntry}
        entry={editingEntry}
        disableNestedSplitDialog
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            setEditingEntry(null)
          }
        }}
        onSaved={updatedEntry => {
          setSessionEntriesDraft(prev =>
            prev.map(sessionEntry =>
              sessionEntry.id === updatedEntry.id ? updatedEntry : sessionEntry,
            ),
          )
          setEditingEntry(null)
        }}
      />
    </>
  )
}
